// Controls Component - Start/Stop, Mode, Scheduling

import React from 'react';
import { useTradingStore } from '../lib/store';

export function Controls() {
  const config = useTradingStore((state) => state.config);
  const isRunning = useTradingStore((state) => state.isRunning);
  const error = useTradingStore((state) => state.error);
  const setConfig = useTradingStore((state) => state.setConfig);
  const startTrading = useTradingStore((state) => state.startTrading);
  const stopTrading = useTradingStore((state) => state.stopTrading);
  const setError = useTradingStore((state) => state.setError);
  
  const [showSchedule, setShowSchedule] = React.useState(false);
  
  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  
  const handleDayToggle = (day: number) => {
    const newDays = config.scheduledDays.includes(day)
      ? config.scheduledDays.filter(d => d !== day)
      : [...config.scheduledDays, day];
    setConfig({ scheduledDays: newDays });
  };

  return (
    <div className="controls">
      <div className="controls-main">
        <div className="mode-selector">
          <label>Mode:</label>
          <select 
            value={config.mode}
            onChange={(e) => setConfig({ mode: e.target.value as 'SIMULATION' | 'LIVE' })}
            disabled={isRunning}
          >
            <option value="SIMULATION">🎮 Simulation</option>
            <option value="LIVE">💰 Live Trading</option>
          </select>
          {config.mode === 'LIVE' && (
            <span className="warning">⚠️ Real money!</span>
          )}
        </div>
        
        <div className="bet-size-control">
          <label>Bet Size:</label>
          <input
            type="number"
            min="0.1"
            max="10"
            step="0.1"
            value={config.betSizePercent}
            onChange={(e) => setConfig({ betSizePercent: parseFloat(e.target.value) || 2 })}
            disabled={isRunning}
          />
          <span>%</span>
        </div>
        
        <div className="refresh-control">
          <label>Refresh:</label>
          <select
            value={config.refreshIntervalMs}
            onChange={(e) => setConfig({ refreshIntervalMs: parseInt(e.target.value) })}
          >
            <option value="10000">10 sec</option>
            <option value="30000">30 sec</option>
            <option value="60000">1 min</option>
            <option value="300000">5 min</option>
          </select>
        </div>
        
        <button
          type="button"
          className={`btn-main ${isRunning ? 'running' : 'stopped'}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            console.log('Start/Stop button clicked, isRunning:', isRunning);
            console.log('startTrading function:', typeof startTrading);
            console.log('stopTrading function:', typeof stopTrading);
            if (!startTrading || !stopTrading) {
              console.error('Functions not available!');
              return;
            }
            try {
              if (isRunning) {
                console.log('Calling stopTrading...');
                stopTrading();
              } else {
                console.log('Calling startTrading...');
                startTrading();
              }
            } catch (error) {
              console.error('Error in start/stop trading:', error);
            }
          }}
          style={{ cursor: 'pointer', pointerEvents: 'auto' }}
        >
          {isRunning ? '⏹️ Stop' : '▶️ Start'} Trading
        </button>
      </div>
      
      <div className="controls-schedule">
        <button 
          className="btn-schedule-toggle"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowSchedule(!showSchedule);
          }}
        >
          {showSchedule ? '▼' : '▶'} Schedule Settings
        </button>
        
        {showSchedule && (
          <div className="schedule-settings">
            <div className="schedule-row">
              <label>
                <input
                  type="checkbox"
                  checked={config.autoTrade}
                  onChange={(e) => setConfig({ autoTrade: e.target.checked })}
                />
                Auto-trade during scheduled hours
              </label>
            </div>
            
            <div className="schedule-row">
              <label>Start Time:</label>
              <input
                type="time"
                value={config.scheduledStart}
                onChange={(e) => setConfig({ scheduledStart: e.target.value })}
              />
              <label>End Time:</label>
              <input
                type="time"
                value={config.scheduledEnd}
                onChange={(e) => setConfig({ scheduledEnd: e.target.value })}
              />
            </div>
            
            <div className="schedule-row">
              <label>Days:</label>
              <div className="day-buttons">
                {weekdays.map((day, index) => (
                  <button
                    key={day}
                    className={`day-btn ${config.scheduledDays.includes(index) ? 'active' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleDayToggle(index);
                    }}
                  >
                    {day}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="schedule-info">
              <p>
                📊 Based on historical analysis:
              </p>
              <ul>
                <li>Best hours: <strong>03:00-04:00 UTC</strong> (10-11pm EST)</li>
                <li>Best day: <strong>Thursday</strong></li>
                <li>Strategy B: +33% avg return at 03:00 UTC</li>
              </ul>
            </div>
          </div>
        )}
      </div>
      
      {error && (
        <div className="error-banner">
          <span>❌ {error}</span>
          <button 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setError(null);
            }}
          >
            ✕
          </button>
        </div>
      )}
      
      <div className="status-bar">
        <span className={`status-indicator ${isRunning ? 'active' : 'inactive'}`}>
          {isRunning ? '🟢 Running' : '🔴 Stopped'}
        </span>
        <span className="mode-indicator">
          {config.mode === 'SIMULATION' ? '🎮 Simulation' : '💰 Live'}
        </span>
        <span className="time-indicator">
          {new Date().toLocaleTimeString()}
        </span>
      </div>
    </div>
  );
}
